import { useState, useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    CartesianGrid,
} from "recharts";

const CustomerActivityChart = ({ data }) => {
    const [range, setRange] = useState("12"); // "12" or "6"

    // ✅ filter data based on select
    const filteredData = useMemo(() => {
        if (!data) return [];

        if (range === "6") {
            return data.slice(0, 6); // get last 6 months
        }
        return data;
    }, [data, range]);

    return (
        <div className="bg-white rounded-[14px] border border-[#e4e5e7] p-[20px] md:p-[24px]">

            {/* Header */}
            <div className="flex items-center justify-between mb-[20px]">
                <h3 className="font-semibold text-[16px] text-[#212326]">
                    Customer Activity
                </h3>

                {/* ✅ now working select */}
                <select
                    value={range}
                    onChange={(e) => setRange(e.target.value)}
                    className="appearance-none bg-[#f4f5f6] border-none rounded-[8px] px-[12px] py-[6px] text-[12px] font-medium text-[#212326] cursor-pointer focus:outline-none"
                >
                    <option value="12">Last 12 months</option>
                    <option value="6">Last 6 months</option>
                </select>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={240}>
                <BarChart data={filteredData} barCategoryGap="25%">

                    <CartesianGrid
                        stroke="#f0f1f3"
                        strokeDasharray="3 3"
                        vertical={false}
                    />

                    <XAxis
                        dataKey="name"
                        tick={{ fill: "#9298a0", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                    />

                    <YAxis
                        tick={{ fill: "#9298a0", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    {[0, 15, 30, 45, 60].map((y) => (
                        <ReferenceLine key={y} y={y} stroke="#f0f1f3" />
                    ))}

                    <Bar
                        dataKey="loyal"
                        fill="#ff5323"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                    />
                    <Bar
                        dataKey="new"
                        fill="#ffa07a"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                    />
                    <Bar
                        dataKey="unique"
                        fill="#f4f5f6"
                        stroke="#e4e5e7"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                    />
                </BarChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="flex items-center gap-[20px] mt-[16px] flex-wrap">
                <LegendItem color="#ff5323" label="Loyal Customers" />
                <LegendItem color="#ffa07a" label="New Customers" />
                <LegendItem color="#f4f5f6" label="Unique Customers" border />
            </div>
        </div>
    );
};

const LegendItem = ({ color, label, border }) => (
    <div className="flex items-center gap-[6px]">
        <div
            className="w-[10px] h-[10px] rounded-[3px]"
            style={{
                background: color,
                border: border ? "1px solid #e4e5e7" : "none",
            }}
        />
        <span className="text-[12px] text-[#717182]">{label}</span>
    </div>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
        <div className="bg-[#212326] text-white text-[12px] px-[14px] py-[10px] rounded-[10px] shadow-md min-w-[80px]">

            {/* Label (month) */}
            <p className="text-[#9298a0] text-[11px] mb-2">
                {label}
            </p>

            {/* List of all bars */}
            <ul className="space-y-1">
                {payload.map((item, index) => (
                    <li
                        key={index}
                        className="flex items-center justify-between gap-3"
                        style={{ color: item.color }}
                    >
                        <span className="capitalize">
                            {item.name} {':'}
                        </span>

                        <span className="text-white font-medium">
                            {item.value}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default CustomerActivityChart;